import React, { useMemo } from 'react'
import styles from './index.module.scss'
import { stateAbbreviations } from './validateUsaState'
import {
  SeFriendlyPageType,
  specialtyValueMappingForBreadcrumbs,
} from './constants'
import { formatCity } from './utils'
import { cn } from '@/lib/utils'
import { THRIVEWORKS_HOME } from 'constants/footer'
import { enhanceUrlWithMarketingParams } from 'lib/marketingParams'

type BreadcrumbLinkProps = {
  href: string
  children: React.ReactNode
  isHub?: boolean
}

const BreadcrumbArrow: React.FC = () => (
  <img src="/icons/breadcrumbs-right-arrow.svg" alt="Breadcrumbs right arrow" />
)

const BreadcrumbLink: React.FC<BreadcrumbLinkProps> = ({
  href,
  children,
  isHub = false,
}) => (
  <div
    className={cn(styles.breadcrumbs__link, {
      [styles.breadcrumbs__link_hub]: isHub,
    })}
  >
    <a href={enhanceUrlWithMarketingParams(href)}>{children}</a>
  </div>
)

const SeFriendlyBreadcrumbs: React.FC<BreadcrumbsProps> = ({
  pageType,
  type,
  state = '',
  city = '',
  specialty = '',
}) => {
  const stateFullSpelling = stateAbbreviations[state]
  const typeSlug = type.toLowerCase()
  const stateSlug = state.toLowerCase()
  const citySlug = formatCity(city)

  const { SPECIALTY, CITY, STATE, HUB } = SeFriendlyPageType

  const isSpecialtyPage = pageType === SPECIALTY
  const isCityPage = pageType === CITY
  const isStatePage = pageType === STATE
  const isHubPage = pageType === HUB

  const specialtyBreadcrumbText = useMemo(() => {
    if (!isSpecialtyPage) {
      return null
    }

    const breadcrumbText = specialtyValueMappingForBreadcrumbs[specialty]

    return breadcrumbText
      ? `${breadcrumbText} in ${city}, ${state}`
      : `Therapists in ${city}, ${state}`
  }, [specialty, isSpecialtyPage, city, state])

  return (
    <div className={styles.breadcrumbs_container}>
      <div className={styles.breadcrumbs}>
        {/* Home icon */}
        <div className={styles.breadcrumbs__icon}>
          <a href={enhanceUrlWithMarketingParams(THRIVEWORKS_HOME)}>
            <img
              src="/icons/breadcrumbs-home.svg"
              width={24}
              height={24}
              alt="Breadcrumbs home"
            />
          </a>
        </div>

        <BreadcrumbArrow />
        <BreadcrumbLink href={`/${typeSlug}`} isHub={isHubPage}>
          {type.charAt(0).toUpperCase() + type.slice(1)}
        </BreadcrumbLink>

        {isStatePage && (
          <>
            <BreadcrumbArrow />
            <div className={styles.breadcrumbs__location}>
              {stateFullSpelling}
            </div>
          </>
        )}

        {isCityPage && (
          <>
            <BreadcrumbArrow />
            <BreadcrumbLink href={`/${typeSlug}/${stateSlug}`}>
              {stateFullSpelling}
            </BreadcrumbLink>
            <BreadcrumbArrow />
            <div className={styles.breadcrumbs__location}>
              {`${city}, ${state}`}
            </div>
          </>
        )}

        {isSpecialtyPage && (
          <>
            <BreadcrumbArrow />
            <BreadcrumbLink href={`/${typeSlug}/${stateSlug}`}>
              {stateFullSpelling}
            </BreadcrumbLink>
            <BreadcrumbArrow />
            <BreadcrumbLink href={`/${typeSlug}/${stateSlug}/${citySlug}`}>
              {`${city}, ${state}`}
            </BreadcrumbLink>
            <BreadcrumbArrow />
            <div className={styles.breadcrumbs__location}>
              {specialtyBreadcrumbText}
            </div>
          </>
        )}
      </div>

      <div className={styles.fade_overlay} />
    </div>
  )
}

export default SeFriendlyBreadcrumbs
